import { ArrowLeft, Info, Heart, Code, Shield, Mail, Globe, Smartphone } from "lucide-react";
import { BottomNav } from "./bottom-nav";
import { SMLogo } from "./sm-logo";

interface AboutPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites" | "wallet";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

export function AboutPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: AboutPageProps) {
  const appVersion = "1.0.0";
  const buildNumber = "100";

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Sobre o Aplicativo</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {/* App Logo & Title */}
        <div className="flex flex-col items-center pt-8 pb-6 px-4">
          <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <SMLogo className="w-16 h-16 text-white" />
          </div>
          <h2 className="text-2xl font-semibold text-foreground mb-1">akihub</h2>
          <p className="text-muted-foreground text-center">
            Seu marketplace de produtos usados
          </p>
        </div>

        {/* Version Info */}
        <div className="mx-4 mb-6">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-semibold text-primary">{appVersion}</div>
                <div className="text-xs text-muted-foreground mt-1">Versão</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-primary">{buildNumber}</div>
                <div className="text-xs text-muted-foreground mt-1">Build</div>
              </div>
              <div>
                <div className="text-sm font-medium text-foreground mt-2">2026</div>
                <div className="text-xs text-muted-foreground mt-1">Ano</div>
              </div>
            </div>
          </div>
        </div>

        {/* App Description */}
        <div className="px-4 mb-6">
          <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-foreground mb-2">O que é o akihub?</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  O akihub é a plataforma brasileira que conecta pessoas que desejam 
                  vender seus produtos usados com compradores em busca de bons negócios.
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Nossa missão é promover o consumo sustentável, dando nova vida a produtos 
                  de qualidade e tornando o comércio de segunda mão simples, seguro e acessível 
                  para todos os brasileiros.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="px-4 mb-6">
          <h3 className="font-medium text-foreground mb-3 px-1">Recursos Principais</h3>
          <div className="space-y-3">
            <div className="bg-card border border-border rounded-xl p-4 flex gap-3">
              <Smartphone className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground text-sm mb-1">Interface Intuitiva</h4>
                <p className="text-xs text-muted-foreground">
                  Design pensado para facilitar a compra e venda de produtos usados
                </p>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-4 flex gap-3">
              <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground text-sm mb-1">Segurança e Privacidade</h4>
                <p className="text-xs text-muted-foreground">
                  Proteção de dados conforme LGPD e transações seguras
                </p>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-xl p-4 flex gap-3">
              <Heart className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground text-sm mb-1">Sustentabilidade</h4>
                <p className="text-xs text-muted-foreground">
                  Incentivamos o reuso e a economia circular
                </p>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 flex gap-3">
              <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground text-sm mb-1">100% Brasileiro</h4>
                <p className="text-xs text-muted-foreground">
                  Desenvolvido para o mercado brasileiro, com suporte em português
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact & Support */}
        <div className="px-4 mb-6">
          <h3 className="font-medium text-foreground mb-3 px-1">Contato e Suporte</h3>
          <div className="bg-card border border-border rounded-xl divide-y divide-border">
            <button
              type="button"
              className="w-full p-4 flex items-center gap-3 hover:bg-accent transition-colors"
              onClick={() => (window.location.href = "mailto:suporte@akihub.com.br")}
            >
              <Mail className="w-5 h-5 text-primary" />
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-foreground">E-mail</div>
                <div className="text-xs text-muted-foreground">suporte@akihub.com.br</div>
              </div>
            </button>
            <button
              type="button"
              className="w-full p-4 flex items-center gap-3 hover:bg-accent transition-colors"
              onClick={() => window.open("https://www.akihub.com.br", "_blank", "noopener,noreferrer")}
            >
              <Globe className="w-5 h-5 text-primary" />
              <div className="flex-1 text-left">
                <div className="text-sm font-medium text-foreground">Website</div>
                <div className="text-xs text-muted-foreground">www.akihub.com.br</div>
              </div>
            </button>
          </div>
        </div>

        {/* Credits */}
        <div className="px-4 mb-8">
          <div className="bg-secondary/30 border border-primary/20 rounded-xl p-4 text-center">
            <Code className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-1">
              Desenvolvido com ❤️ no Brasil
            </p>
            <p className="text-xs text-muted-foreground">
              © 2026 akihub. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}